"use client";

import {
  getCountryCodeFromLocale,
  getCurrencyByLocale,
  getTabbyConfiguredCountries,
  getTabbyMerchantCode,
  getTamaraConfiguredCountries,
  getTamaraScriptSource,
  getValuConfiguredCountries,
} from "@/apiUtils/commonUtils";
import { PaymentOptionItem, TranslationData, VariantItemPdp } from "@/common/lib/types";
import { tabbyPaymentPublicKey, tamaraPaymentPublicKey } from "@/runtimeConfig/public";
import Image from "next/image";
import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import InfoIcon from "@/assets/icons/info.svg?react";
import Script from "next/script";
import ValuIcon from "@/assets/icons/valu.svg";
import { applyBnplTemplate, getBnplPlanInfo, BnplPlanService } from "@/utils/bnpl.utils";
import RenderPrice from "@/components/productPrice/RenderPrice";

interface IProps {
  locale: string;
  selectedVariant: VariantItemPdp | undefined;
  lang: "ar" | "en";
  translations: TranslationData;
  discountedPrice: number;
  installments?: PaymentOptionItem[];
}

const PaymentOptions = ({
  locale,
  lang,
  selectedVariant,
  translations,
  discountedPrice,
  installments,
}: IProps) => {
  const [isTabbyLoaded, setIsTabbyLoaded] = useState(false);
  const tabbyRef = useRef<HTMLDivElement>(null);
  const country = getCountryCodeFromLocale(locale);
  const isTamaraEnabled = getTamaraConfiguredCountries().includes(country);
  const isTabbyEnabled = getTabbyConfiguredCountries().includes(country);
  const isValuEnabled = getValuConfiguredCountries().includes(country);
  const tamaraPublicKey = tamaraPaymentPublicKey;
  const tamaraScriptSource = getTamaraScriptSource();
  const tabbyScriptSource = process.env.NEXT_PUBLIC_TABBY_SCRIPT_SRC;
  const tabbyMerchantCode = getTabbyMerchantCode(country);
  const currency = selectedVariant?.pricing?.price.currency;
  const displayCurrency = getCurrencyByLocale(locale);
  const tabbyPublicKey = tabbyPaymentPublicKey;
  const getInstallmentItem = (type: string) =>
    installments?.find((item) => item?.type === type && item?.enable !== false);
  const getInstallmentLogo = (type: string) => getInstallmentItem(type)?.media?.url;
  const tabbyLogo = getInstallmentLogo("tabby");
  const tamaraLogo = getInstallmentLogo("tamara");
  const valuLogo = getInstallmentLogo("valu") ?? ValuIcon;
  const tabbyInstallmentsCount = getInstallmentItem("tabby")?.totalInstallments ?? 3;
  const pdpStaticContent = translations?.pdp;

  const planInfo = useMemo(
    () =>
      getBnplPlanInfo(
        installments,
        { tabby: isTabbyEnabled, tamara: isTamaraEnabled },
        discountedPrice,
      ),
    [installments, isTabbyEnabled, isTamaraEnabled, discountedPrice],
  );

  const handleScriptLoad = () => {
    setIsTabbyLoaded(true);
  };

  const tamaraScript = useMemo(() => {
    if (typeof window !== "undefined") {
      window.tamaraWidgetConfig = {
        lang,
        country: country.toUpperCase(),
        publicKey: tamaraPublicKey,
        css: ".tamara-summary-widget__content:hover{ backgroundColor: transparent} .tamara-summary-widget--inline-outlined{ border: none!important; padding: 0!important;}.option svg{width: 1rem; height: 1rem;}.option img { width: 2.9rem }.option{display: flex; gap:0.25rem; align-items: center}",
      };
    }
    return <Script src={tamaraScriptSource} strategy="lazyOnload" defer />;
  }, [country, lang, tamaraPublicKey, tamaraScriptSource]);

  useEffect(() => {
    if (isTabbyLoaded && window.TabbyPromo) {
      try {
        new window.TabbyPromo({
          selector: "#TabbyPromo",
          currency,
          price: discountedPrice,
          // installmentsCount: 3,
          lang,
          source: "product",
          publicKey: tabbyPublicKey,
          merchantCode: tabbyMerchantCode,
          class: "overflow-auto",
        });
      } catch (error) { }
    }
  }, [currency, discountedPrice, isTabbyLoaded, lang, tabbyMerchantCode, tabbyPublicKey]);

  if (!isTabbyEnabled && !isTamaraEnabled && !isValuEnabled) {
    return null;
  }

  /** Renders CMS template text from Sanity with bold placeholder values. */
  const renderMessage = (
    template: string | undefined,
    values: Record<string, string | number>,
    monthlyAmount?: number,
  ): ReactNode | null => {
    if (!template?.trim()) {
      return null;
    }

    const parts = template.split(/(\{\{months\}\}|\{\{monthlyAmount\}\})/g).filter(Boolean);
    return (
      <span className="inline">
        {parts.map((part, index) => {
          if (part === "{{months}}") {
            return (
              <span key={`bnpl_months_${index}`} className="font-semibold text-black">
                {values.months ?? ""}
              </span>
            );
          }

          if (part === "{{monthlyAmount}}") {
            if (monthlyAmount === undefined) {
              return null;
            }
            return (
              <RenderPrice
                key={`bnpl_amount_${index}`}
                price={monthlyAmount}
                currencyCode={displayCurrency}
                lang={lang}
                className="font-semibold text-black"
              />
            );
          }

          const textAfterAmount = index > 0 && parts[index - 1] === "{{monthlyAmount}}";
          if (textAfterAmount) {
             const match = part.match(/^(\s*\/\s*month)(.*)$/i);
             if (match) {
              return (
                <React.Fragment key={`bnpl_text_${index}`}>
                <span className="font-semibold text-black">
                  {match[1]}
                </span>
                  {match[2]}
                </React.Fragment>
              );
            }
          }

          return <React.Fragment key={`bnpl_text_${index}`}>{part}</React.Fragment>;
        })}
      </span>
    );
  };

  const renderBadge = (template: string | undefined, values: Record<string, string | number> = {}): ReactNode | null => {
    const text = applyBnplTemplate(template, values);
    if (!text) {
      return null;
    }
    return text;
  };

  const renderTabbyLogo = (logoUrl: string) => (
    <span
      role="presentation"
      className="items relative inline-flex items-center cursor-pointer align-middle"
    >
      <Image
        src={logoUrl}
        alt={lang === "en" ? "Tabby" : "تابي"}
        width={120}
        height={36}
        className="h-[16px] w-auto"
      />
      <div
        id="TabbyPromo"
        ref={tabbyRef}
        aria-hidden="true"
        className="absolute inset-0 h-full w-full opacity-0 overflow-hidden"
      ></div>
    </span>
  );

  const renderTamaraLogo = (logoUrl: string) => (
    <tamara-widget inline-type="0" className="">
      <span role="button" className="option items flex items-center">
        <Image
          src={logoUrl}
          alt={lang === "en" ? "Tamara" : "تمارا"}
          width={120}
          height={16}
          className="h-[16px] w-auto"
        />
      </span>
    </tamara-widget>
  );

  const renderServiceLogo = (service: BnplPlanService) =>
    service.type === "tabby" ? renderTabbyLogo(service.logoUrl) : renderTamaraLogo(service.logoUrl);

  const renderValuOption = () =>
    isValuEnabled ? (
      <span className="option items flex items-center gap-1">
        <Image src={valuLogo} alt="value" width={20} height={20} className="w-10 text-black" />
        <InfoIcon className="h-4 text-tertiary-6" />
      </span>
    ) : null;

  const badgeClassName =
    "inline-block rounded-lg bg-[#E9F4E7] px-3 py-1.5 text-[13px] font-medium text-black break-words max-w-full";

  const renderScenarioContent = () => {
    const { scenario, services, months } = planInfo;

    if (scenario === "oneService") {
      const [service] = services;
      const badge = renderBadge(pdpStaticContent?.pdp_bnpl_one_service_badge);
      const message = renderMessage(
        pdpStaticContent?.pdp_bnpl_single_service_message,
        { months: service.totalInstallments },
        service.monthlyAmount,
      );
      return (
        <div className="pt-2">
          {badge && <div className={badgeClassName}>{badge}</div>}
          <div className="flex flex-wrap items-center gap-2 pt-2">
            {message && <span className="text-sm text-tertiary-6">{message}</span>}
            {renderServiceLogo(service)}
            {renderValuOption()}
          </div>
        </div>
      );
    }

    if (scenario === "samePlan") {
      const badge = renderMessage(pdpStaticContent?.pdp_bnpl_same_plan_badge, {
        count: services.length,
        months: months ?? "",
      });
      const message = renderMessage(
        pdpStaticContent?.pdp_bnpl_same_plan_message,
        { months: months ?? "" },
        services[0].monthlyAmount,
      );
      return (
        <div className="pt-2">
          {badge && <div className={badgeClassName}>{badge}</div>}
          {message && <div className="pt-2 text-sm text-tertiary-6">{message}</div>}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {services.map((service) => (
              <React.Fragment key={`bnpl_logo_${service.type}`}>{renderServiceLogo(service)}</React.Fragment>
            ))}
            {renderValuOption()}
          </div>
        </div>
      );
    }

    // differentPlans
    const badge = renderMessage(pdpStaticContent?.pdp_bnpl_different_plans_badge, {
      count: services.length,
      planCount: services.length,
    });
    return (
      <div className="pt-2">
        {badge && <div className={badgeClassName}>{badge}</div>}
        {services.map((service, index) => {
          const message = renderMessage(
            pdpStaticContent?.pdp_bnpl_different_plans_message,
            { months: service.totalInstallments },
            service.monthlyAmount,
          );
          return (
            <div
              className={`text-sm text-tertiary-6${index === 0 ? " pt-2" : " pt-1"}`}
              key={`bnpl_row_${service.type}`}
            >
              {message}
              {message && <span className="inline-block align-middle mx-1">{renderServiceLogo(service)}</span>}
              {!message && renderServiceLogo(service)}
            </div>
          );
        })}
        {isValuEnabled && <div className="flex items-center pt-2">{renderValuOption()}</div>}
      </div>
    );
  };

  // Legacy rendering, used when totalInstallments is not configured in Sanity
  const renderFallbackContent = () => (
    <div className="flex flex-wrap items-center gap-1 pt-2">
      <span className="text-sm text-tertiary-6">
        {translations?.common?.common_interest_free_installment_message}
      </span>
      {isTabbyEnabled && tabbyLogo && (
        <span
          role="presentation"
          className="items relative flex items-center gap-1 cursor-pointer"
        >
          <Image
            src={tabbyLogo}
            alt={lang === "en" ? "Tabby" : "تابي"}
            width={120}
            height={36}
            className="h-[16px] w-auto"
          />
          <InfoIcon className="h-4 text-tertiary-6" />
          <div
            id="TabbyPromo"
            ref={tabbyRef}
            aria-hidden="true"
            className="absolute inset-0 h-full w-full opacity-0 overflow-hidden"
          ></div>
        </span>
      )}

      {isTamaraEnabled && tamaraLogo && (
        <>
          <div className="text-sm text-tertiary-6">or</div>
          <tamara-widget inline-type="0" className="">
            <span className="option items flex items-center gap-1">
              <Image
                src={tamaraLogo}
                alt={lang === "en" ? "Tamara" : "تمارا"}
                width={120}
                height={16}
                className="h-[16px] w-auto"
              />
              <InfoIcon className="h-4 text-tertiary-6" />
            </span>
          </tamara-widget>
        </>
      )}

      {renderValuOption()}
    </div>
  );

  return (
    <>
      {tamaraScript}
      {<Script src={tabbyScriptSource} strategy="afterInteractive" onLoad={handleScriptLoad} />}
      {planInfo.scenario === "fallback" ? renderFallbackContent() : renderScenarioContent()}
    </>
  );
};

export default PaymentOptions;